import { useRef, useState } from 'react';
import QRCode from 'react-qr-code';

const SIZE_PRESETS = [
  { label: 'Small (15mm)', mm: 15 },
  { label: 'Medium (25mm)', mm: 25 },
  { label: 'Large (40mm)', mm: 40 },
  { label: 'X-Large (60mm)', mm: 60 },
];

/**
 * Reusable QR print sheet — controls + printable grid + SVG export.
 * Used by both the item print page and the personal-handle print page.
 *
 *   <QrPrintSheet
 *     url="https://e.ezbounty.com/I/ABCD2345"
 *     defaultText="Cash reward!"
 *     name="old putter"          // optional — enables "show name" toggle
 *     bountyText="$25"           // optional — enables "show bounty" toggle
 *     filenameBase="ezbounty-ABCD2345"
 *   />
 *
 * Presentational: it owns the print-related UI state (quantity, size,
 * text, toggles) but knows nothing about items vs handles — callers pass
 * the url and optional extras.
 */
export default function QrPrintSheet({ url, defaultText = 'Cash reward!', name, bountyText, filenameBase = 'ezbounty-qr' }) {
  const [qty, setQty] = useState(6);
  const [sizeMm, setSizeMm] = useState(25);
  const [customMm, setCustomMm] = useState('');
  const [text, setText] = useState(defaultText);
  const [showName, setShowName] = useState(false);
  const [showBounty, setShowBounty] = useState(false);

  const sheetRef = useRef(null);

  const effMm = customMm ? Math.max(8, Math.min(120, parseFloat(customMm) || sizeMm)) : sizeMm;
  const cells = Array.from({ length: Math.max(1, Math.min(60, qty)) });

  function downloadSvg() {
    const px = 300;
    const qrSvg = sheetRef.current?.querySelector('svg');
    if (!qrSvg) return;
    const inner = qrSvg.innerHTML;
    const vb = qrSvg.getAttribute('viewBox') || `0 0 ${px} ${px}`;

    const lines = [];
    if (text) lines.push({ t: text, weight: 700, size: 20 });
    if (showName && name) lines.push({ t: name, weight: 400, size: 14 });
    if (showBounty && bountyText) lines.push({ t: bountyText, weight: 700, size: 18 });

    let labelSvg = '';
    lines.forEach((ln, i) => {
      labelSvg += `<text x="${px / 2}" y="${px + 24 + i * 20}" text-anchor="middle" font-family="system-ui,sans-serif" font-weight="${ln.weight}" font-size="${ln.size}">${escapeXml(ln.t)}</text>`;
    });

    const totalH = px + (lines.length ? 24 + lines.length * 20 : 0);
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${px} ${totalH}" width="${effMm}mm">
      <rect width="100%" height="100%" fill="#fff"/>
      <svg viewBox="${vb}" width="${px}" height="${px}">${inner}</svg>
      ${labelSvg}
    </svg>`;

    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${filenameBase}.svg`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <>
      <div className="print-controls">
        <div className="card p-20 mb-24">
          <div className="field">
            <label>Quantity</label>
            <input type="number" min={1} max={60} value={qty}
              onChange={(e) => setQty(parseInt(e.target.value) || 1)} />
            <div className="hint">How many copies on the sheet (1–60).</div>
          </div>

          <div className="field">
            <label>Size</label>
            <div className="chips">
              {SIZE_PRESETS.map((p) => (
                <button key={p.mm} type="button"
                  onClick={() => { setSizeMm(p.mm); setCustomMm(''); }}
                  className={`btn toggle-btn${!customMm && sizeMm === p.mm ? ' is-on' : ''}`}
                  aria-pressed={!customMm && sizeMm === p.mm}>
                  {p.label}
                </button>
              ))}
            </div>
            <div className="row gap-8 mt-10">
              <span className="muted tiny">Custom:</span>
              <input type="number" min={8} max={120} value={customMm} placeholder="mm"
                onChange={(e) => setCustomMm(e.target.value)} className="input-mm" aria-label="Custom size in millimetres" />
              <span className="muted tiny">mm</span>
            </div>
          </div>

          <div className="field">
            <label>Label text</label>
            <input type="text" maxLength={40} value={text}
              onChange={(e) => setText(e.target.value)} placeholder="e.g. Cash reward!" />
            <div className="hint">Shown under each QR. Leave blank for none.</div>
          </div>

          {(name || bountyText) && (
            <div className="field">
              <label>Also show</label>
              {name && (
                <label className="check-label">
                  <input type="checkbox" checked={showName} onChange={(e) => setShowName(e.target.checked)} />
                  Name ("{name}")
                </label>
              )}
              {bountyText && (
                <label className="check-label mt-6">
                  <input type="checkbox" checked={showBounty} onChange={(e) => setShowBounty(e.target.checked)} />
                  Bounty amount ({bountyText})
                </label>
              )}
            </div>
          )}

          <div className="row gap-10 mt-8">
            <button type="button" className="btn btn-primary" onClick={() => window.print()}>
              Print / Save as PDF
            </button>
            <button type="button" className="btn btn-ghost" onClick={downloadSvg}>
              Download SVG (one label)
            </button>
          </div>
          <div className="hint mt-10">
            "Print / Save as PDF" opens your print dialog — choose a printer, or
            "Save as PDF" as the destination for a print-ready file.
          </div>
        </div>
      </div>

      <div className="print-sheet" ref={sheetRef}>
        {cells.map((_, i) => (
          <div className="qr-cell" key={i} style={{ width: `${effMm}mm` }}>
            <QRCode value={url} size={256} level="M" />
            {text && <div className="qr-label">{text}</div>}
            {showName && name && <div className="qr-name">{name}</div>}
            {showBounty && bountyText && <div className="qr-bounty">{bountyText}</div>}
          </div>
        ))}
      </div>
    </>
  );
}

function escapeXml(s = '') {
  return String(s)
    .replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;').replaceAll("'", '&#39;');
}
